import {
  CreateRouteRequest,
  CreateRouteResponse,
  EndCustomer,
  Route,
  RouteCalculationResponse,
  RouteDetailsResponse,
  UpdateRouteRequest,
  VehicleRoutingRequest,
} from "@/interfaces";
import api from "@/lib/axios";
import { errorHandler } from "@/lib/utils";
import { DefaultGetRequestParams, DefaultResponse } from "./types";

export interface NoRouteMessageResponse {
  message: string;
  route: string;
}

export const routeService = {
  async index(params: DefaultGetRequestParams = { page: 1, per_page: 10 }) {
    try {
      const { page, per_page, search, filters } = params;
      const response = await api.get<DefaultResponse<Route>>("/routes", {
        params: {
          page,
          per_page,
          search,
          ...filters,
        },
      });
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao buscar rotas.");
      throw err;
    }
  },

  async startRoute(id: number) {
    try {
      const response = await api.patch<DefaultResponse<void>>(
        `/routes/${id}/start`,
      );
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao iniciar rota.");
      throw err;
    }
  },

  async finishRoute(id: number) {
    try {
      const response = await api.patch<DefaultResponse<void>>(
        `/routes/${id}/finish`,
      );
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao finalizar rota.");
      throw err;
    }
  },

  async indexAll() {
    try {
      const response =
        await api.get<DefaultResponse<Route[], false>>("/routes/all");
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao buscar todas as rotas.");
      throw err;
    }
  },

  async show(id: number) {
    try {
      const response = await api.get<DefaultResponse<Route, false>>(
        `/routes/${id}`,
      );
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao buscar detalhes da rota.");
      throw err;
    }
  },

  async getDetailedRoute(id: number) {
    try {
      const response = await api.get<DefaultResponse<Route, false>>(
        `/routes/${id}/details`,
      );
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao buscar detalhes da rota.");
      throw err;
    }
  },

  async create(data: CreateRouteRequest) {
    try {
      const response = await api.post<
        DefaultResponse<CreateRouteResponse, false>
      >("/routes", data);
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao criar rota.");
      throw err;
    }
  },

  async update(id: number, data: UpdateRouteRequest) {
    try {
      const response = await api.put<DefaultResponse<Route>>(
        `/routes/${id}`,
        data,
      );
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao atualizar rota.");
      throw err;
    }
  },

  async duplicateRoute(
    id: number,
    data: { vehicle_id: number; driver_id: number; date: string },
  ) {
    try {
      const response = await api.post<DefaultResponse<Route, false>>(
        `/routes/${id}/duplicateRoute`,
        data,
      );
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao duplicar rota.");
      throw err;
    }
  },

  async destroy(id: number) {
    try {
      const response = await api.delete(`/routes/${id}`);
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao deletar rota.");
      throw err;
    }
  },

  async markAsDone(id: number) {
    try {
      const response = await api.patch(`/routes/${id}/done`);
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao marcar rota como concluída.");
      throw err;
    }
  },

  async getRouteDetails(id: number) {
    try {
      const response = await api.get<
        DefaultResponse<RouteDetailsResponse, false>
      >(`/routes/${id}/details`);
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao buscar detalhes da rota.");
      throw err;
    }
  },

  async getNoRouteMessage() {
    try {
      const response = await api.get<NoRouteMessageResponse | null>(
        "/routes/check_route_prerequisites",
      );
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao buscar mensagem de rota não encontrada.");
      throw err;
    }
  },

  async calculateVehicleRouting(data: VehicleRoutingRequest) {
    try {
      const response = await api.post<
        DefaultResponse<RouteCalculationResponse, false>
      >("/calcute_vehicle_routing", data);
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao calcular rota do veículo.");
      throw err;
    }
  },
};

export const endCustomerService = {
  async index(params: DefaultGetRequestParams = { page: 1, per_page: 10 }) {
    try {
      const { page, per_page, search, filters } = params;
      const response = await api.get<DefaultResponse<EndCustomer>>(
        "/end_customers",
        {
          params: {
            page,
            per_page,
            search,
            ...filters,
          },
        },
      );
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao buscar clientes.");
      throw err;
    }
  },

  async searchAddress(address: string) {
    try {
      const response = await api.post<{
        errors: boolean;
        data: {
          data: {
            latitude: string;
            longitude: string;
            city: string;
            primary_address: string;
            [key: string]: string;
          };
        } | null;
      }>("/end_customers/search_address", { address });
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao buscar endereço.");
      throw err;
    }
  },

  async indexAll() {
    try {
      const response =
        await api.get<DefaultResponse<EndCustomer[], false>>(
          "/end_customers/all",
        );
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao buscar todos os clientes.");
      throw err;
    }
  },

  async show(id: number) {
    try {
      const response = await api.get<DefaultResponse<EndCustomer, false>>(
        `/end_customers/${id}`,
      );
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao buscar detalhes do cliente.");
      throw err;
    }
  },

  async store(data: Omit<EndCustomer, "id" | "created_at" | "updated_at">) {
    try {
      const response = await api.post<DefaultResponse<EndCustomer>>(
        "/end_customers",
        data,
      );
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao criar cliente.");
      throw err;
    }
  },

  async update(id: number, data: Partial<EndCustomer>) {
    try {
      const response = await api.put<DefaultResponse<EndCustomer>>(
        `/end_customers/${id}`,
        data,
      );
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao atualizar cliente.");
      throw err;
    }
  },

  async destroy(id: number) {
    try {
      const response = await api.delete(`/end_customers/${id}`);
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao deletar cliente.");
      throw err;
    }
  },

  async searchCEP(data: { cep: string }) {
    try {
      const response = await api.post("/end_customers/search_cep", data);
      return response.data;
    } catch (err) {
      errorHandler(err, "Erro ao buscar CEP.");
      throw err;
    }
  },
};

export default routeService;
